import type {
  ConsolidatedCenterLayerData,
  EnrichedCareerEntity,
} from '@/domains/centerLayer/centerLayer.entity';
import type { DayName } from '@/domains/shared';

/**
 * Filtros para extracción de carreras
 */
interface ExtractCareersFilters {
  jornada: DayName | null;
}

/**
 * Use Case para extraer carreras de CenterLayer
 * Cumple SRP: Solo se encarga de extraer y filtrar carreras
 * Cumple OCP: Abierto a extensión (nuevos filtros) cerrado a modificación
 *
 * Responsabilidades:
 * - Filtrar carreras por jornada (si aplica)
 * - Consolidar carreras de múltiples jornadas
 * - Retornar carreras enriquecidas con jornada y cod_compuesto
 */
export class ExtractCareersUseCase {
  /**
   * Extrae carreras según filtro de jornada
   * @param apiData - Datos consolidados con estructura por jornada
   * @param jornada - Jornada específica o null para consolidar ambas
   * @returns Array de carreras enriquecidas
   */
  private extractCareers(
    apiData: ConsolidatedCenterLayerData,
    jornada: DayName | null,
  ): EnrichedCareerEntity[] {
    if (jornada) {
      return apiData[jornada]?.carreras ?? [];
    }

    const diurnoCareers = apiData.diurno?.carreras ?? [];
    const vespertinoCareers = apiData.vespertino?.carreras ?? [];

    return [...diurnoCareers, ...vespertinoCareers];
  }

  /**
   * Ejecuta la extracción de carreras
   * @param apiData - Datos consolidados del centro
   * @param filters - Filtros de jornada
   * @returns Carreras enriquecidas con jornada y cod_compuesto
   */
  execute(
    apiData: ConsolidatedCenterLayerData | null,
    filters: ExtractCareersFilters,
  ): EnrichedCareerEntity[] {
    if (!apiData) return [];

    return this.extractCareers(apiData, filters.jornada);
  }
}
